import type {EagGridState, EagGridStore} from '../store/createEagGridStore';
import type {ActionCallback} from '../types/ActionCallback';
import type {RowData} from '../types/RowData';
import {isThenable} from './isThenable';

/**
 * The slice of the grid store the bulk execution core reads and mutates. The full
 * EagGridStore satisfies it structurally; tests can supply a minimal object.
 */
type BulkActionStore = Pick<EagGridStore, 'deselectAll' | 'setIsBulkActionLoading'> & {
  getState: () => Pick<EagGridState, 'isBulkActionLoading' | 'queryId'>;
};

type ExecuteBulkActionParams = {
  store: BulkActionStore;
  actionCallback: ActionCallback<readonly string[]> | undefined;
  rows: RowData[];
  rowIds: string[];
  refreshData: () => void;
  actionKey: string;
};

/**
 * Single execution core for a bulk action, shared by the in-grid BulkActionsBar
 * (rowIds derived from selectedRowIds) and the external invoke channel (rowIds from
 * the event). Assembles the same actionCallback param object the native bulk path
 * used, keeps the isBulkActionLoading double-submit guard and Promise lifecycle.
 * `getColumnData`/`sourceSearchParams`/`refreshCells`/`redrawRows`/`actionLock` are
 * intentionally stubbed for bulk (parity with the previous inline implementation).
 */
export const executeBulkAction = (params: ExecuteBulkActionParams): void => {
  if (!params.actionCallback) {
    return;
  }
  if (params.store.getState().isBulkActionLoading) {
    return;
  }

  // Only rows that actually offer the action may be targeted — the invoke channel is a
  // public API, so a caller-supplied actionKey/rowIds combination is not trusted. The
  // in-grid path is unaffected: its bulk bar only offers actions present on every
  // selected row (intersection semantics).
  const idSet = new Set(params.rowIds);
  const targetRows = params.rows.filter(
    (row) =>
      idSet.has(row.id) &&
      (row.actions.primary.includes(params.actionKey) ||
        row.actions.secondary.includes(params.actionKey))
  );
  if (targetRows.length === 0) {
    return;
  }

  const result = params.actionCallback({
    actionKey: params.actionKey,
    rowId: targetRows.map((row) => row.id),
    rowData: targetRows,
    refreshData: params.refreshData,
    refreshCells: () => undefined,
    redrawRows: () => undefined,
    deselectAll: () => params.store.deselectAll(),
    getColumnData: () => null,
    sourceSearchParams: '',
    queryId: params.store.getState().queryId,
    actionLock: {lock: () => undefined, unlock: () => undefined},
  });

  if (isThenable(result)) {
    params.store.setIsBulkActionLoading(true);
    Promise.resolve(result)
      .catch(() => {
        // Consumer handles error display (toast, etc.)
      })
      .finally(() => {
        params.store.setIsBulkActionLoading(false);
      });
  }
};
